import type { ParentType } from '@risksmart-app/domain/src/types/consts';
import { approvalResourceTypes } from '@risksmart-app/shared/approvals/workflows';
import { useQueries } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useTRPC } from 'src/utils/trpc';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';

/**
 * Resolves the set of approval resource types (ParentTypes) the current user is
 * permitted to configure, by checking the `${resourceType}_approval` Manager
 * permission for every resource type that owns an approval workflow.
 *
 * tRPC-only — only runs when the `trpc` flag is on. With the flag off it returns
 * `null` for `manageableResourceTypes`, signalling callers to fall back to the
 * legacy behaviour (server-side Hasura gating). Batches the per-resource-type `frontend.approval.canManageApproval` check across all approval resource types
 * so the global settings surface can offer only what the viewer can configure.
 */
export const useManageableApprovalResourceTypes = () => {
  const trpc = useTRPC();
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');

  const results = useQueries({
    queries: approvalResourceTypes.map((resourceType) => ({
      ...trpc.frontend.approval.canManageApproval.queryOptions({
        resourceType,
      }),
      enabled: trpcEnabled,
    })),
  });

  const loading = trpcEnabled && results.some((r) => r.isLoading);

  const manageableResourceTypes = useMemo((): ParentType[] | null => {
    // `null` both with the flag off (legacy fallback — server-side Hasura
    // gating) AND while ANY per-resource check is still loading: a
    // half-resolved list would momentarily exclude resource types whose
    // query simply hasn't answered yet, flickering workflow options and
    // gating. Consumers treat `null` as "no client filtering" and gate
    // mutating affordances on `loading`.
    if (!trpcEnabled || loading) {
      return null;
    }

    // ApprovalResourceType is a subset of ParentType (the workflows map keys),
    // so the filtered array is assignable without a cast.
    return approvalResourceTypes.filter(
      (_resourceType, index) => results[index]?.data === true
    );
  }, [trpcEnabled, loading, results]);

  return { manageableResourceTypes, loading };
};
